<template>
  <div v-if="fieldLoader" class="d-flex flex-wrap align-items-center justify-content-start gap-2">
    {{ loadingText }}
  </div>
  <div v-else class="d-flex flex-wrap align-items-center justify-content-start gap-2">
    <el-select
      ref="infinite-dropdown"
      v-if="!computedReadOnly"
      value=""
      :class="[{ invalid: isInvalid }, 'stretch']"
      :id="`${fieldId}-${name}-datalist`"
      :name="name"
      :no-data-text="noDataText"
      :no-match-text="noDataText"
      :placeholder="placeholder"
      :size="inputSize"
      allow-create
      default-first-option
      filterable
      multiple
      @change="onChange"
      @visible-change="onVisibleChange"
    >
      <el-option v-for="{ key, value } in optionsToRender" :key="key" :label="value" :value="key" />
    </el-select>
    <el-tag
      v-for="tag in selection"
      :key="tag.key"
      :closable="!computedReadOnly"
      :disable-transitions="false"
      :effect="effect"
      :size="tagSize"
      :type="tagType"
      @close="onRemoveTag(tag)"
    >
      {{ tag.value }}
    </el-tag>
  </div>
</template>

<script>
import { cloneDeep, get, isEmpty, isEqual, pullAllWith, unionBy } from 'lodash'
import { isObject } from '../../../utils'
import dropdownMixin from '../../../utils/mixin-dropdown'

export default {
  name: 'TagSelect',
  mixins: [dropdownMixin],
  data() {
    return {
      newId: 0,
      effect: this.computedReadOnly ? '' : 'dark',
      inputSize: '',
      selection: [],
      tagType: '',
      tagSize: 'small',
    }
  },
  computed: {
    /**
     * @description Readonly eseten ez a tartalom jelenik meg a mezo helyen
     * @returns {string}
     */
    content() {
      const value = !Array.isArray(this.value) ? [] : this.value
      if (this.dropdownHasValue) {
        const stringValues = value.map((selectedItem) => {
          if (isObject(selectedItem)) {
            return selectedItem.key
          }
          return String(selectedItem)
        })
        const items = this.options.filter((elem) => stringValues.includes(String(elem.key)))
        return items.map((item) => item.value).join(', ')
      }
      return ''
    },
    /**
     * @description A kivalasztott tagek id[]-je, vagy null
     * @returns {[]|null}
     */
    selectionValue() {
      return Array.isArray(this.value) ? this.value : null
    },
    /**
     * @description A dropdownban megjelenitendo elemek (tag eseten a kivalasztottak nelkul)
     * @returns {*[]}
     */
    optionsToRender() {
      return this.options.filter(
        (option) => option && !this.selection.find((selected) => selected.key.toString() === option.key.toString())
      )
    },
    /**
     * @description Ertekvaltozaskor az emittalando value a valasztott elemek id, vagy az uj tag objectek []-je,
     * @returns {(any)[]}
     */
    valueToEmit() {
      return (
        Array.isArray(this.selection) && this.selection.map((value) => (this.isNewTag(value.key) ? value : value.key))
      )
    },
  },
  methods: {
    /**
     * @description Id-vel nem rendelkezo, uj elem hozzaadasa a selectionhoz, es optionshoz
     * @param value
     */
    addNewTag(value) {
      this.selection = unionBy(this.selection, [{ key: `new-${this.newId}`, value }], 'value')
      this.options = unionBy(this.options, [{ key: `new-${this.newId}`, value }], 'value')
      this.newId++
    },
    /**
     * @description Kapott, meg nem letezo tagek kezelese
     * @param newValue
     */
    handleNewTaggableValue(newValue) {
      if (!Array.isArray(newValue) && newValue.length) {
        newValue = [newValue]
      }
      newValue.forEach((value) => {
        if (isObject(value) && this.isNewTag(value)) {
          this.selection = unionBy(this.selection, [value], 'key')
        } else {
          this.onChange(value, false)
        }
      })
      this.$emit('change', { value: this.valueToEmit, options: this.options })
    },
    /**
     * @description Visszaadja, hogy a kapott tag tablaban meg nem letezo elem-e
     * @param key
     * @returns {RegExpMatchArray}
     */
    isNewTag(key) {
      return key.toString().match(/^new-/) // @regex matches 'new-' string at start
    },
    onChange(value, emitChange = true) {
      const selectedOption = this.options.find((option) => String(option.key) === String(value[0]))
      if (selectedOption) {
        this.selection = unionBy(this.selection, [selectedOption], 'value')
      } else {
        // uj tag
        this.addNewTag(String(value[0]))
      }
      if (emitChange) this.$emit('change', { value: this.valueToEmit, options: this.options })
      // elemhozzaadaskor csokken a lista, mintha scrolloznank lefele...
      if (this.isOpen) this.onScrollOptions()
    },
    onRemoveTag(tag) {
      this.selection.splice(this.selection.indexOf(tag), 1)
      this.$emit('change', { value: this.valueToEmit, options: this.options })
    },
    /**
     * @description Lista prop valtozaskor az eddig kivalasztott elemek letezeset a listaban leellenorizzuk
     */
    updateSelection() {
      if (!Array.isArray(this.value)) return
      this.value.forEach((selectedTagKey) => {
        if (isObject(selectedTagKey) && this.isNewTag(selectedTagKey.key)) {
          // ez egy uj tag
          this.selection = unionBy(this.selection, [selectedTagKey], 'value')
        } else {
          const options = get(this.list, 'options', [])
          const selectedTag = options.find((tag) => tag.key === selectedTagKey)
          if (selectedTag) {
            this.selection = unionBy(this.selection, [selectedTag], 'value')
          }
        }
      })
    },
    valueWatchHandler(nV, oV) {
      const newValue = cloneDeep(nV)
      const oldValue = cloneDeep(oV)
      if (!isEmpty(newValue) && !isEqual(newValue, oldValue)) {
        const selection = cloneDeep(this.selectionValue)
        const hasNewValues = pullAllWith(newValue, selection, isEqual)
        if (hasNewValues.length) {
          this.handleNewTaggableValue(hasNewValues)
        }
      }
    },
  },
  watch: {
    list: {
      immediate: true,
      handler(newList, oldList) {
        if (!isEqual(newList, oldList)) {
          this.updateSelection()
        }
      },
    },
  },
}
</script>
